import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SubscriptionProvider } from "@/context/SubscriptionContext";
import { Toaster } from 'sonner';
import Layout from "@/components/layout/Layout";
import SessionWarning from "@/components/SessionWarning";
import Login from "@/pages/Login";
import TestAllReceipts from "@/pages/TestAllReceipts";
import TenantLogin from "@/pages/TenantLogin";
import AutoLogin from "@/pages/group/AutoLogin";
import LandingPage from "@/pages/LandingPage";
import StartTrial from "@/pages/StartTrial";
import PurchasePage from "@/pages/PurchasePage";
import TenantRedirect from "@/pages/TenantRedirect";
import TenantApp from "@/pages/TenantApp";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ForcePasswordChange from "@/pages/ForcePasswordChange";
import Dashboard from "@/pages/Dashboard";
import StaffAttendanceMark from "@/pages/staff/StaffAttendanceMark";
import ProfileSettings from "@/pages/ProfileSettings";
import CollegeSettings from "@/pages/settings/CollegeSettings";
import AttendanceSettings from "@/pages/settings/AttendanceSettings";
import DataExportImport from "@/pages/settings/DataMigration";
import PublicAdmissionForm from "@/pages/public/PublicAdmissionForm";
import AdmissionReceipt from "@/pages/public/AdmissionReceipt";
import PolicyPage from "@/pages/public/PolicyPage";
import TenantPolicyPage from "@/pages/public/TenantPolicyPage";
import DataMigration from "@/pages/admin/DataMigration";
import DiagnoseFees from "@/pages/admin/DiagnoseFees";
import EmailSettings from "@/pages/admin/EmailSettings";
import ActivityLog from "@/pages/admin/ActivityLog";
import BackupPage from "@/pages/admin/BackupPage";
import TenantPaymentGateway from "@/pages/settings/TenantPaymentGateway";
import SMSSettings from "@/pages/settings/SMSSettings";
import WhatsAppSettings from "@/pages/settings/WhatsAppSettings";
import PolicyManager from "@/pages/settings/PolicyManager";
import ManualCertificatePrint from "@/pages/certificates/ManualCertificatePrint";
import CertificateHistory from "@/pages/certificates/CertificateHistory";
import CertificateSettings from "@/pages/certificates/CertificateSettings";
import SendNotification from "@/pages/notifications/SendNotification";
import StaffMessages from "@/pages/notifications/StaffMessages";

// Student Portal Pages
import StudentLogin from "@/pages/student-portal/StudentLogin";
import StudentDashboard from "@/pages/student-portal/StudentDashboard";
import StudentFormFill from "@/pages/student-portal/StudentFormFill";
import StudentFees from "@/pages/student-portal/StudentFees";
import StudentAttendance from "@/pages/student-portal/StudentAttendance";
import StudentResults from "@/pages/student-portal/StudentResults";
import StudentProfile from "@/pages/student-portal/StudentProfile";
import StudentChangePassword from "@/pages/student-portal/StudentChangePassword";
import StudentHostel from "@/pages/student-portal/StudentHostel";
import StudentTransport from "@/pages/student-portal/StudentTransport";
import StudentLibrary from "@/pages/student-portal/StudentLibrary";
import StudentRoutine from "@/pages/student-portal/StudentRoutine";
import StudentLMS from "@/pages/student-portal/StudentLMS";
import StudentMessages from "@/pages/student-portal/StudentMessages";
import StudentProgress from "@/pages/student-portal/StudentProgress";
import StudentReceipts from "@/pages/student-portal/StudentReceipts";
import WithdrawnInfo from "@/pages/student-portal/WithdrawnInfo";
import StudentReceiptView from "@/pages/student-portal/StudentReceiptView";
import StudentDocuments from "@/pages/student-portal/StudentDocuments";
import StudentJobPortal from "@/pages/placement/StudentJobPortal";

// Staff Portal Pages
import StaffLogin from "@/pages/staff-portal/StaffLogin";
import StaffDashboard from "@/pages/staff-portal/StaffDashboard";
import StaffProfile from "@/pages/staff-portal/StaffProfile";
import StaffAttendanceHistory from "@/pages/staff-portal/StaffAttendanceHistory";
import StaffChangePassword from "@/pages/staff-portal/StaffChangePassword";
import StaffSalary from "@/pages/staff-portal/StaffSalary";
import StaffLeave from "@/pages/staff-portal/StaffLeave";
import StaffNotifications from "@/pages/staff-portal/StaffNotifications";
import StaffPortalMessages from "@/pages/staff-portal/StaffPortalMessages";
import StaffRegularization from "@/pages/staff-portal/StaffRegularization";
import MyClasses from "@/pages/staff-portal/MyClasses";
import MarkAttendance from "@/pages/staff-portal/MarkAttendance";
import EnterMarks from "@/pages/staff-portal/EnterMarks";
import StaffDocuments from "@/pages/staff-portal/StaffDocuments";
import StaffBankDetails from "@/pages/staff-portal/StaffBankDetails";
import StaffExamAttendance from "@/pages/staff-portal/ExamAttendance";
import CompanyPortal from "@/pages/placement/CompanyPortal";

// Teacher LMS Pages
import TeacherLMS from "@/pages/staff-portal/TeacherLMS";
import TeacherContent from "@/pages/staff-portal/TeacherContent";
import TeacherAssignments from "@/pages/staff-portal/TeacherAssignments";
import TeacherSubmissions from "@/pages/staff-portal/TeacherSubmissions";
import TeacherQuizzes from "@/pages/staff-portal/TeacherQuizzes";
import TeacherOnlineClasses from "@/pages/staff-portal/TeacherOnlineClasses";
import TeacherAnnouncements from "@/pages/staff-portal/TeacherAnnouncements";
import TeacherDiscussions from "@/pages/staff-portal/TeacherDiscussions";
import TeacherAnalytics from "@/pages/staff-portal/TeacherAnalytics";

// Import all route modules
import {
  superAdminRoutes,
  groupAdminRoutes,
  adminRoutes,
  masterSettingsRoutes,
  studentRoutes,
  feesRoutes,
  hrRoutes,
  libraryRoutes,
  inventoryRoutes,
  hostelRoutes,
  transportRoutes,
  consultantRoutes,
  seatBookingRoutes,
  admissionRoutes,
  attendanceRoutes,
  routineRoutes,
  examinationRoutes,
  leaveRoutes,
  accountsRoutes,
  reportsRoutes,
  frontDeskRoutes,
  communicationRoutes,
  transcriptsRoutes,
  ticketRoutes,
  roleRoutes,
  userManagementRoutes,
  lmsRoutes,
  settingsRoutes,
  placementRoutes,
  alumniRoutes,
  scholarshipRoutes,
  researchRoutes,
  mouRoutes,
  iqacRoutes,
  naacDashboardRoutes,
  alumniPortalRoutes,
  auditRoutes
} from "@/routes";

// Import Alumni Portal for public route
import AlumniPortal from '@/pages/alumni-portal/AlumniPortal';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Protected Layout wrapper
const ProtectedLayout = ({ children }) => {
  return (
    <ProtectedRoute>
      <Layout>
        {children}
      </Layout>
    </ProtectedRoute>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SubscriptionProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/test/all-receipts" element={<TestAllReceipts />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/company-portal" element={<CompanyPortal />} />
          <Route path="/alumni-portal" element={<AlumniPortal />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/force-password-change" element={<ForcePasswordChange />} />
          <Route path="/admission-form/:tenantId" element={<PublicAdmissionForm />} />
          <Route path="/online-admission/:tenantId" element={<PublicAdmissionForm />} />
          <Route path="/public/admission/:tenantId" element={<PublicAdmissionForm />} />
          <Route path="/public/admission-form/:tenantId" element={<PublicAdmissionForm />} />
          <Route path="/public/admission-form" element={<PublicAdmissionForm />} />
          <Route path="/public/admission-receipt/:applicationId" element={<AdmissionReceipt />} />
          
          {/* Policy Pages - Main Site */}
          <Route path="/policy/:type" element={<PolicyPage />} />
          
          {/* Student Portal Routes - Public (no auth required) */}
          <Route path="/student/login" element={<StudentLogin />} />
          <Route path="/student/dashboard" element={<StudentDashboard />} />
          <Route path="/student/withdrawn" element={<WithdrawnInfo />} />
          <Route path="/student/form-fill" element={<StudentFormFill />} />
          <Route path="/student/fees" element={<StudentFees />} />
          <Route path="/student/attendance" element={<StudentAttendance />} />
          <Route path="/student/results" element={<StudentResults />} />
          <Route path="/student/profile" element={<StudentProfile />} />
          <Route path="/student/documents" element={<StudentDocuments />} />
          <Route path="/student/change-password" element={<StudentChangePassword />} />
          <Route path="/student/hostel" element={<StudentHostel />} />
          <Route path="/student/transport" element={<StudentTransport />} />
          <Route path="/student/library" element={<StudentLibrary />} />
          <Route path="/student/receipts" element={<StudentReceipts />} />
          <Route path="/student/receipt/:receiptNo" element={<StudentReceiptView />} />
          <Route path="/student/lms" element={<StudentLMS />} />
          <Route path="/student/messages" element={<StudentMessages />} />
          <Route path="/student/progress" element={<StudentProgress />} />
          <Route path="/student/routine" element={<StudentRoutine />} />
          <Route path="/student/jobs" element={<StudentJobPortal />} />
          
          {/* Staff Portal Routes - Public (no auth required) */}
          <Route path="/staff-portal" element={<StaffLogin />} />
          <Route path="/staff-portal/login" element={<StaffLogin />} />
          <Route path="/staff-portal/dashboard" element={<StaffDashboard />} />
          <Route path="/staff-portal/profile" element={<StaffProfile />} />
          <Route path="/staff-portal/attendance" element={<StaffAttendanceHistory />} />
          <Route path="/staff-portal/change-password" element={<StaffChangePassword />} />
          <Route path="/staff-portal/salary" element={<StaffSalary />} />
          <Route path="/staff-portal/leave" element={<StaffLeave />} />
          <Route path="/staff-portal/notifications" element={<StaffNotifications />} />
          <Route path="/staff-portal/messages" element={<StaffPortalMessages />} />
          <Route path="/staff-portal/regularization" element={<StaffRegularization />} />
          <Route path="/staff-portal/my-classes" element={<MyClasses />} />
          <Route path="/staff-portal/mark-attendance/:assignmentId" element={<MarkAttendance />} />
          <Route path="/staff-portal/enter-marks/:assignmentId" element={<EnterMarks />} />
          <Route path="/staff-portal/exam-attendance" element={<StaffExamAttendance />} />
          <Route path="/staff-portal/documents" element={<StaffDocuments />} />
          <Route path="/staff-portal/bank-details" element={<StaffBankDetails />} />
          
          {/* Teacher LMS Routes */}
          <Route path="/staff-portal/lms" element={<TeacherLMS />} />
          <Route path="/staff-portal/lms/content" element={<TeacherContent />} />
          <Route path="/staff-portal/lms/assignments" element={<TeacherAssignments />} />
          <Route path="/staff-portal/lms/submissions" element={<TeacherSubmissions />} />
          <Route path="/staff-portal/lms/quizzes" element={<TeacherQuizzes />} />
          <Route path="/staff-portal/lms/classes" element={<TeacherOnlineClasses />} />
          <Route path="/staff-portal/lms/announcements" element={<TeacherAnnouncements />} />
          <Route path="/staff-portal/lms/discussions" element={<TeacherDiscussions />} />
          <Route path="/staff-portal/lms/analytics" element={<TeacherAnalytics />} />
          
          {/* SuperAdmin Login */}
          <Route path="/superadmin/login" element={<Login />} />
          
          {/* Dashboard - Direct route */}
          <Route path="/dashboard" element={<ProtectedLayout><Dashboard /></ProtectedLayout>} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/start-trial" element={<StartTrial />} />
          <Route path="/purchase" element={<PurchasePage />} />
          
          {/* Profile & Settings */}
          <Route path="/profile" element={<ProtectedLayout><ProfileSettings /></ProtectedLayout>} />
          <Route path="/college-settings" element={<ProtectedLayout><CollegeSettings /></ProtectedLayout>} />
          <Route path="/settings/attendance" element={<ProtectedLayout><AttendanceSettings /></ProtectedLayout>} />
          <Route path="/data-export" element={<ProtectedLayout><DataExportImport /></ProtectedLayout>} />
          <Route path="/settings/payment-gateway" element={<ProtectedLayout><TenantPaymentGateway /></ProtectedLayout>} />
          <Route path="/settings/sms" element={<ProtectedLayout><SMSSettings /></ProtectedLayout>} />
          <Route path="/settings/whatsapp" element={<ProtectedLayout><WhatsAppSettings /></ProtectedLayout>} />
          <Route path="/admin-migration" element={<ProtectedLayout><DataMigration /></ProtectedLayout>} />
          <Route path="/diagnose-fees" element={<ProtectedLayout><DiagnoseFees /></ProtectedLayout>} />
          <Route path="/admin/email-settings" element={<ProtectedLayout><EmailSettings /></ProtectedLayout>} />
          <Route path="/admin/activity-log" element={<ProtectedLayout><ActivityLog /></ProtectedLayout>} />
          <Route path="/admin/backup" element={<ProtectedLayout><BackupPage /></ProtectedLayout>} />
          <Route path="/certificates/manual-print" element={<ProtectedLayout><ManualCertificatePrint /></ProtectedLayout>} />
          <Route path="/certificates/history" element={<ProtectedLayout><CertificateHistory /></ProtectedLayout>} />
          <Route path="/certificates/settings" element={<ProtectedLayout><CertificateSettings /></ProtectedLayout>} />
          <Route path="/staff/mark-attendance" element={<ProtectedLayout><StaffAttendanceMark /></ProtectedLayout>} />
          <Route path="/notifications/send" element={<ProtectedLayout><SendNotification /></ProtectedLayout>} />
          <Route path="/notifications/staff-messages" element={<ProtectedLayout><StaffMessages /></ProtectedLayout>} />
          
          {/* SuperAdmin Routes */}
          {React.Children.map(superAdminRoutes.props.children, (route, index) => {
            if (!route) return null;
            const path = route.props.path.startsWith('/') ? route.props.path : `/${route.props.path}`;
            return (
              <Route 
                key={`superadmin-${index}`}
                path={path}
                element={<ProtectedLayout>{route.props.element}</ProtectedLayout>}
              />
            );
          })}
          
          {/* GroupAdmin Routes */}
          {React.Children.map(groupAdminRoutes.props.children, (route, index) => {
            if (!route) return null;
            const path = route.props.path.startsWith('/') ? route.props.path : `/${route.props.path}`;
            return (
              <Route 
                key={`groupadmin-${index}`}
                path={path}
                element={<ProtectedLayout>{route.props.element}</ProtectedLayout>}
              />
            );
          })}
          
          {/* All Module Routes */}
          {[
            adminRoutes,
            masterSettingsRoutes,
            studentRoutes,
            feesRoutes,
            hrRoutes,
            libraryRoutes,
            inventoryRoutes,
            hostelRoutes,
            transportRoutes,
            consultantRoutes,
            seatBookingRoutes,
            admissionRoutes,
            userManagementRoutes,
            attendanceRoutes,
            routineRoutes,
            examinationRoutes,
            leaveRoutes,
            accountsRoutes,
            reportsRoutes,
            frontDeskRoutes,
            communicationRoutes,
            transcriptsRoutes,
            ticketRoutes,
            roleRoutes,
            lmsRoutes,
            settingsRoutes,
            placementRoutes,
            alumniRoutes,
            scholarshipRoutes,
            researchRoutes,
            mouRoutes,
            iqacRoutes,
            naacDashboardRoutes,
            auditRoutes
          ].flatMap((routeGroup, groupIndex) => {
            if (!routeGroup?.props?.children) return [];
            const children = Array.isArray(routeGroup.props.children) 
              ? routeGroup.props.children 
              : [routeGroup.props.children];
            return children.map((route, index) => {
              if (!route?.props?.path) return null;
              const path = route.props.path.startsWith('/') ? route.props.path : `/${route.props.path}`;
              return (
                <Route 
                  key={`route-${groupIndex}-${index}`}
                  path={path}
                  element={<ProtectedLayout>{route.props.element}</ProtectedLayout>}
                />
              );
            }).filter(Boolean);
          })}
          
          {/* Tenant Routes - Must be after all reserved paths */}
          <Route path="/:tenantCode" element={<TenantRedirect />} />
          <Route path="/:tenantCode/login" element={<TenantLogin />} />
          <Route path="/:tenantCode/auto-login" element={<AutoLogin />} />
          
          {/* Tenant Policy Pages - Public */}
          <Route path="/:tenant/policy/:type" element={<TenantPolicyPage />} />
          
          {/* Tenant-Specific Portal Routes */}
          <Route path="/:tenantCode/student/login" element={<StudentLogin />} />
          <Route path="/:tenantCode/student/dashboard" element={<StudentDashboard />} />
          <Route path="/:tenantCode/student/withdrawn" element={<WithdrawnInfo />} />
          <Route path="/:tenantCode/student/fees" element={<StudentFees />} />
          <Route path="/:tenantCode/student/form-fill" element={<StudentFormFill />} />
          <Route path="/:tenantCode/student/profile" element={<StudentProfile />} />
          <Route path="/:tenantCode/student/documents" element={<StudentDocuments />} />
          <Route path="/:tenantCode/student/receipts" element={<StudentReceipts />} />
          <Route path="/:tenantCode/staff-portal" element={<StaffLogin />} />
          <Route path="/:tenantCode/alumni-portal" element={<AlumniPortal />} />
          <Route path="/:tenantCode/alumni-portal/login" element={<AlumniPortal />} />
          <Route path="/:tenantCode/company-portal" element={<CompanyPortal />} />
          
          <Route path="/:tenantCode/*" element={<TenantApp />} />
          
          {/* 404 Fallback */}
          <Route path="*" element={
            <ProtectedLayout>
              <div className="p-8 text-center">
                <h1 className="text-2xl font-bold text-gray-800">404 - Page Not Found</h1>
              </div>
            </ProtectedLayout>
          } />
        </Routes>
        <SessionWarning />
        <Toaster position="top-right" richColors />
        </SubscriptionProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
